import React, { useEffect, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Paper,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// customerViewService import removed - using sessionStorage directly

const CustomerView: React.FC = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  // Get customer info from sessionStorage (tab-specific)
  const [customerViewState, setCustomerViewState] = useState<{ customerId: number; customerName: string } | null>(null);
  
  useEffect(() => {
    // Check sessionStorage for customer view
    const sessionData = sessionStorage.getItem('customerView');
    if (sessionData) {
      try {
        const data = JSON.parse(sessionData);
        setCustomerViewState(data);
      } catch (error) {
        console.error('Error parsing customer view data:', error);
      }
    }
  }, []);
  
  const customerId = customerViewState?.customerId;
  const customerName = customerViewState?.customerName || 'Unknown Customer';
  
  useEffect(() => {
    // Check if user is authorized
    if (!member || (member.memberType !== 'master_admin' && member.memberType !== 'reseller_admin')) {
      navigate('/customers');
      return;
    }
    
    if (!customerId) {
      // No customer view state, go back to customers
      navigate('/customers');
      return;
    }
    
    // Set document title
    document.title = `Customer View - ${customerName}`;
    
    // Simulate loading
    setTimeout(() => setIsLoading(false), 500);
  }, [member, customerId, customerName, navigate]);
  
  const handleOpenInNewTab = () => {
    // The dashboard will read from session storage, no need for URL params
    window.open('/dashboard', '_blank');
  };
  
  const handleClose = () => {
    // Clear customer view state from sessionStorage (tab-specific)
    sessionStorage.removeItem('customerView');
    navigate('/customers');
  };
  
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header Bar */}
      <AppBar position="static" sx={{ bgcolor: '#9c27b0' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Customer View
          </Typography>
          
          <Chip
            label={`Viewing as: ${customerName}`}
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              mr: 2,
              fontWeight: 500
            }}
          />
          
          <Button
            color="inherit"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
            sx={{ mr: 2 }}
          >
            Open in New Tab
          </Button>
          
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      {/* Notice Bar */}
      <Alert 
        severity="info" 
        sx={{ 
          borderRadius: 0,
          '& .MuiAlert-icon': { fontSize: '1.5rem' }
        }}
      >
        <Typography variant="body2">
          You are viewing the portal as <strong>{customerName}</strong> (Customer ID: {customerId}).
          This is a read-only view for administrative purposes.
        </Typography>
      </Alert>
      
      {/* Main Content - iframe */}
      <Box sx={{ flexGrow: 1, p: 2 }}>
        <Paper 
          elevation={3}
          sx={{ 
            height: '100%', 
            overflow: 'hidden',
            borderRadius: 2,
            bgcolor: 'white'
          }}
        >
          {!isLoading && (
            <iframe
              src="/dashboard"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '8px'
              }}
              title={`Customer View - ${customerName}`}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default CustomerView;